import { ArchiveX, CheckCircle2, Save } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useSendClaimEvent, useUpdatePassenger } from "../../api/use-claim-mutations";
import { getCivilityTitle } from "../passengers/passenger-grouping";

function isValidEmail(email: string | undefined): boolean {
  return Boolean(z.string().email().safeParse(email?.trim()).success);
}

/**
 * Port of states-cta/ForceArchivePanel.vue — missing passenger email:
 * fill emails then confirm, or archive the claim.
 */
export function ForceArchivePanelBody({ claim }: { claim: ClaimWithRelations }) {
  const sendClaimEvent = useSendClaimEvent(claim.id);
  const updatePassenger = useUpdatePassenger(claim.id);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [usersEmail, setUsersEmail] = useState<Record<number, string>>({});

  const passengersWithoutEmail = claim.usersClaims
    .filter(({ email, user }) => {
      const effectiveEmail = email || user.email;
      return !effectiveEmail || effectiveEmail.endsWith("upclaim.eu");
    })
    .map(({ user, userSegment }) => ({
      userSegmentId: userSegment.id,
      userId: user.id,
      civility: `${getCivilityTitle(user.civility)} ${user.firstName} ${user.lastName?.toUpperCase() ?? ""}`,
    }));

  const allEmailsFilled = passengersWithoutEmail.length === 0;

  function updatePassengerEmail(userSegmentId: number, email: string) {
    updatePassenger.mutate({ userSegmentId, email: email.trim() });
  }

  function confirmEmailReceived() {
    sendClaimEvent.mutate({ machineEvent: "Email passager obtenu" });
  }

  function confirmArchive() {
    sendClaimEvent.mutate({
      machineEvent: "Abandon – Absence d'email",
      internalNote: internalNote || "Archivé par l'agence - Absence d'email passager",
    });
    setShowConfirmModal(false);
    setInternalNote("");
  }

  function closeModal() {
    setShowConfirmModal(false);
    setInternalNote("");
  }

  return (
    <div className="mx-4 my-6 rounded-lg border border-gray-200 bg-white text-left shadow-sm sm:rounded-lg">
      <div className="px-6 py-5 sm:p-6">
        {/* Section 1: Add passenger email */}
        <h3 className="text-base font-semibold text-gray-900">Email voyageur manquant</h3>
        <p className="mt-1 text-sm text-gray-500">
          L'adresse email du voyageur n'a pas été trouvée dans la réservation. Veuillez la renseigner
          ci-dessous ou archiver le dossier.
        </p>

        <div className="mt-4 space-y-4">
          {passengersWithoutEmail.map((passenger) => (
            <div key={passenger.userSegmentId} className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="block text-sm font-medium text-gray-700">{passenger.civility}</Label>
                <Input
                  className="mt-1"
                  value={usersEmail[passenger.userSegmentId] ?? ""}
                  onChange={(e) =>
                    setUsersEmail((emails) => ({
                      ...emails,
                      [passenger.userSegmentId]: e.target.value,
                    }))
                  }
                  onKeyUp={(e) => {
                    if (e.key === "Enter" && isValidEmail(usersEmail[passenger.userSegmentId])) {
                      updatePassengerEmail(passenger.userSegmentId, usersEmail[passenger.userSegmentId]);
                    }
                  }}
                  autoComplete="off"
                  type="email"
                  placeholder="Saisir l'email du passager"
                />
              </div>
              <Button
                disabled={!isValidEmail(usersEmail[passenger.userSegmentId])}
                onClick={() =>
                  updatePassengerEmail(passenger.userSegmentId, usersEmail[passenger.userSegmentId])
                }
              >
                <Save aria-hidden="true" />
                Enregistrer
              </Button>
            </div>
          ))}
        </div>

        {/* Section 2: Email received */}
        {allEmailsFilled ? (
          <div className="mt-6 border-t border-gray-200 pt-5">
            <Button disabled={sendClaimEvent.isPending} onClick={confirmEmailReceived}>
              <CheckCircle2 aria-hidden="true" />
              Email passager obtenu
            </Button>
          </div>
        ) : null}

        {/* Section 3: Archive */}
        <div className="mt-6 border-t border-gray-200 pt-5">
          <p className="mb-3 text-sm font-medium text-gray-500">
            Si l'email n'est pas disponible, vous pouvez archiver le dossier :
          </p>
          <Button variant="destructive" onClick={() => setShowConfirmModal(true)}>
            <ArchiveX aria-hidden="true" />
            Archiver ce dossier
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            closeModal();
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmer l'archivage</DialogTitle>
            <DialogDescription>
              Vous êtes sur le point d'archiver ce dossier en raison de l'absence d'email voyageur. Cette
              action est irréversible.
            </DialogDescription>
          </DialogHeader>

          <DialogPanel>
            <Label htmlFor="archive-note" className="block text-sm font-medium text-gray-700">
              Note (optionnelle)
            </Label>
            <Textarea
              id="archive-note"
              rows={3}
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Raison de l'archivage..."
              className="mt-1"
            />
          </DialogPanel>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmArchive} disabled={sendClaimEvent.isPending}>
              Archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
